
import { Subscription } from 'rxjs/Subscription';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { mapGetters, mapState } from 'vuex';
import { EVENTS } from '../../bus/events.const';
import { logger } from '../../logger';
import { EmpathyXConfig, ScrollDirection } from '../../models';
import { GETTERS_KEYS, SUBMODULES_PATHS } from '../../store';
import { SmartComponentMixin } from '../mixins/smart-component.mixin';

const MAX_SCROLL_TO_ANIMATE = 4000;
const SCROLL_CSS_CLASS = 'ebx-scroll';

// TODO Make this component request results until there is scroll
@Component({
  computed: {
    ...mapState(SUBMODULES_PATHS.core, ['scrollEnabled']),
    ...mapState(SUBMODULES_PATHS.config, ['device']),
    ...mapGetters({ config: GETTERS_KEYS.config.config })
  },
  mixins: [SmartComponentMixin('Scroll')]
})
export default class Scroll extends Vue {
  readonly config: EmpathyXConfig;
  readonly device: string;
  readonly scrollEnabled: boolean;
  domScrollPanel: HTMLElement = null;
  eventsBusSubscriptions: Subscription;
  offsetHeight = 0;
  scrollDirection = this.$store.state.core.scrollDirection;
  scrollHeight = 0;
  scrollOffset = this.$store.state.core.scrollOffset;
  scrollTop = 0;

  get domScrollPanelSelector(): string {
    return this.config.scroll.scrollPanelSelector;
  }

  get hasScrollEnded(): boolean {
    return this.domScrollPanel && this.scrollHeight === this.scrollTop + this.offsetHeight;
  }

  get hasScrollReachedTop(): boolean {
    return this.domScrollPanel && this.scrollTop === 0;
  }

  get isScrollAboutToEnd(): boolean {
    return this.domScrollPanel && this.scrollDistanceToBottom < this.config.scroll.distanceToBottomInPx;
  }

  get scrollDistanceToBottom() {
    return this.scrollHeight - this.scrollTop - this.offsetHeight;
  }

  mounted() {
    this.eventsBusSubscriptions = new Subscription()
      .add(this.$x.on(EVENTS.RebindDomEvents).subscribe(this.selectNewDomScrollPanel))
      .add(this.$x.on(EVENTS.ScrollTo).subscribe(this.syncScroll));
    // We do this here because using an { immediate: true } modifier on the watcher does not guarantee that the dom panel is present
    // Mounted is executed before redrawing the dom, for this reason nextTick is necessary
    Vue.nextTick(this.selectNewDomScrollPanel);
    Object.defineProperty(this, '$el', {
      // The DOM element needs that both properties has to be set. This is necessary for the tests.
      // We have to assign a get to return the element which we assign to $el
      // And a 'get' has to be with a 'set'
      get(): any { return this.domScrollPanel; },
      set(_: any): void { return; }
    });
  }

  beforeDestroy() {
    this.resetOldScrollPanel(this.domScrollPanel);
    this.eventsBusSubscriptions.unsubscribe();
  }

  @Watch('scrollTop')
  emitIsScrollAboutToEnd() {
    if (this.isScrollAboutToEnd) {
      this.$x.emit(EVENTS.ScrollAboutToEnd, this.scrollTop);
    }
  }

  @Watch('scrollDirection')
  emitScrollDirectionChanged(scrollDirection: ScrollDirection) {
    this.$x.emit(EVENTS.ScrollDirectionChanged, scrollDirection);
  }

  @Watch('scrollTop')
  emitScrollEnded() {
    if (this.hasScrollEnded) {
      this.$x.emit(EVENTS.ScrollEnded, this.scrollTop);
    }
  }

  @Watch('scrollOffset')
  emitScrollOffsetChanged(scrollOffset: boolean) {
    this.$x.emit(EVENTS.ScrollOffsetChanged, scrollOffset);
  }

  @Watch('scrollTop')
  emitScrollReachedTop() {
    if (this.hasScrollReachedTop) {
      this.$x.emit(EVENTS.ScrollReachedTop);
    }
  }

  @Watch('domScrollPanel')
  onDomSelectorChange(newDomScrollPanel: HTMLElement, oldDomScrollPanel: HTMLElement) {
    this.resetOldScrollPanel(oldDomScrollPanel);
    this.updateNewScrollPanel();
    if (oldDomScrollPanel) { // Avoid emitting Scroll event in the first scroll panel selector
      this.storeScrollProperties();
    }
  }

  @Watch('scrollEnabled', { immediate: true })
  onScrollEnabled(scrollEnabled: boolean) {
    if (this.domScrollPanel) {
      if (scrollEnabled) {
        this.domScrollPanel.style.overflowY = '';
      } else {
        this.domScrollPanel.scrollTop = 0;
        this.domScrollPanel.style.overflowY = 'hidden';
      }
    }
  }

  render() {}

  @Watch('domScrollPanelSelector')
  @Watch('device')
  selectNewDomScrollPanel() {
    this.domScrollPanel = document.querySelector<HTMLElement>(this.domScrollPanelSelector);
  }

  @Watch('scrollTop')
  storeScrollDirection(newScrollTop: number, oldScrollTop: number) {
    const scrollDelta = oldScrollTop - newScrollTop;
    if (Math.abs(scrollDelta) > this.config.scroll.minimumDeltaInPx || newScrollTop === 0) {
      if (scrollDelta < 0) {
        this.scrollDirection = ScrollDirection.DOWN;
      } else if (scrollDelta > 0) {
        this.scrollDirection = ScrollDirection.UP;
      }
    }
  }

  @Watch('scrollTop')
  storeScrollOffset(newScrollTop: number) {
    const scrollOffsetInPx = this.config.scroll.scrollOffsetInPx;
    if (scrollOffsetInPx > 0) {
      this.scrollOffset = newScrollTop > scrollOffsetInPx;
    }
  }

  protected resetOldScrollPanel(oldDomScrollPanel?: HTMLElement) {
    oldDomScrollPanel?.removeEventListener('scroll', this.storeScrollPropertiesThrottled);
    oldDomScrollPanel?.classList.remove(SCROLL_CSS_CLASS);
  }

  protected storeScrollProperties() {
    const { scrollTop, offsetHeight, scrollHeight } = this.domScrollPanel;
    if (scrollTop >= 0 && (scrollTop + offsetHeight) <= scrollHeight) { // Prevents bouncing events
      this.$x.emit(EVENTS.Scroll, scrollTop);
      this.scrollTop = scrollTop;
      this.scrollHeight = scrollHeight;
      this.offsetHeight = offsetHeight;
    }
  }

  protected storeScrollPropertiesThrottled() {
    window.requestAnimationFrame(this.storeScrollProperties);
  }

  protected syncScroll(scrollTop: number) {
    if (this.domScrollPanel) {
      if (Math.abs(this.scrollTop - scrollTop) > MAX_SCROLL_TO_ANIMATE) {
        this.domScrollPanel.scrollTop = scrollTop;
      } else {
        this.domScrollPanel.scroll({ behavior: 'smooth', left: 0, top: scrollTop });
      }
    }
  }

  protected updateNewScrollPanel() {
    if (this.domScrollPanel) {
      this.domScrollPanel.addEventListener('scroll', this.storeScrollPropertiesThrottled, { passive: true });
      this.domScrollPanel.classList.add(SCROLL_CSS_CLASS);
    } else {
      logger.warn('[SCROLL]', `The scroll element does not exist: ${ this.domScrollPanelSelector }`);
    }
  }
}
